import { logError, logWarn } from 'shared-utils/log'

import type { Notifier } from '@airbrake/browser'

const REPORTED_KEYS_STORAGE_KEY = 'airbrake-reported-keys'

function getReportedKeys (): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(REPORTED_KEYS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []

    return new Set(Array.isArray(parsed) ? parsed : [])
  } catch {
    return new Set()
  }
}

function persistReportedKeys (keys: Set<string>): void {
  try {
    window.sessionStorage.setItem(REPORTED_KEYS_STORAGE_KEY, JSON.stringify([...keys]))
  } catch {
    // sessionStorage unavailable or quota exceeded
  }
}

/**
 * Reports an error to Airbrake. Never throws: reporting a failure must not become one.
 */
export async function reportError (airbrake: Notifier, message: string, error?: Error): Promise<void> {
  try {
    const noticeId = await airbrake.notify({
      message,
      error,
      userAgent: window.navigator?.userAgent || '',
      screen: {
        width: `${window.screen?.width || 0} px (${window.screen?.availWidth || 0} px)`,
        height: `${window.screen?.height || 0} px (${window.screen?.availHeight || 0} px)`,
        orientation: window.screen?.orientation?.type || ''
      }
    })

    if (!noticeId) {
      throw new Error('noticeId is empty')
    }
  } catch (err) {
    const reportingError = err as Error

    logWarn('reportError', 'Report failed', reportingError)
  }
}

/**
 * Same as `reportError`, reported once per key for the lifetime of the tab.
 */
export async function reportErrorOnce (
  airbrake: Notifier,
  dedupKey: string,
  message: string,
  error?: Error
): Promise<void> {
  const keys = getReportedKeys()

  if (keys.has(dedupKey)) {
    return
  }

  keys.add(dedupKey)
  persistReportedKeys(keys)

  await reportError(airbrake, message, error)
}

/**
 * Single channel for a caught failure: Airbrake when it is up, gated console otherwise
 * (local development, or the airbrake plugin failing to instantiate its own notifier).
 */
export function reportScopedError (scope: string, message: string, error: Error): void {
  // Both branches swallow their own failures, so this never throws back into the calling catch.
  if (import.meta.client && window.airbrake) {
    void reportError(window.airbrake, `[${scope}] ${message}`, error)

    return
  }

  logError(scope, message, error)
}

/**
 * Same as `reportScopedError`, reported once per key for the lifetime of the tab (loops, listeners).
 */
export function reportScopedErrorOnce (scope: string, dedupKey: string, message: string, error: Error): void {
  if (import.meta.client && window.airbrake) {
    void reportErrorOnce(window.airbrake, dedupKey, `[${scope}] ${message}`, error)

    return
  }

  logError(scope, message, error)
}

export function resetReportedKeys (): void {
  try {
    window.sessionStorage.removeItem(REPORTED_KEYS_STORAGE_KEY)
  } catch {
    // sessionStorage unavailable
  }
}
